import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { patrolApi, adminApi, portalApi } from '../services/portalApi';
import { fmtDateTime } from '../utils/date';
import { useAuthStore } from '../store/authStore';
import type { PatrolSession, AlarmEvent, IncidentEvent, Guard } from '../types';

interface Props {
  isAdmin?: boolean;
}

// ── Timestamp normaliser ───────────────────────────────────────────────────────
function toMs(ts: string | number | null | undefined): number {
  if (ts == null) return 0;
  if (typeof ts === 'number') return ts < 1_000_000_000_000 ? ts * 1000 : ts;
  const n = Number(ts);
  if (Number.isFinite(n) && n > 0) return n < 1_000_000_000_000 ? n * 1000 : n;
  return new Date(ts).getTime() || 0;
}

// ── Unified event types ────────────────────────────────────────────────────────
type AlarmItem    = { kind: 'alarm';    ts: number; patrolId: string; data: AlarmEvent };
type IncidentItem = { kind: 'incident'; ts: number; patrolId: string; data: IncidentEvent };
type ReportItem   = { kind: 'report';  ts: number; patrolId: string; data: PatrolSession };
type EventItem    = AlarmItem | IncidentItem | ReportItem;

// ── Type badge ─────────────────────────────────────────────────────────────────
function TypeBadge({
  kind,
  onClick,
}: {
  kind: EventItem['kind'];
  onClick?: () => void;
}) {
  const label = kind === 'alarm' ? 'Alarm' : kind === 'incident' ? 'Incident' : 'Patrol Report';

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-sm text-brand-600 hover:underline cursor-pointer whitespace-nowrap"
        title="View patrol route"
      >
        {label}
      </button>
    );
  }
  return (
    <span className="text-sm text-gray-700 whitespace-nowrap">{label}</span>
  );
}

// ── Row components ─────────────────────────────────────────────────────────────

const CELL = 'px-4 py-3 text-sm text-gray-700';
const DASH = <span className="text-gray-300">—</span>;

/** Alarm row: GPS + Ack columns populated */
function AlarmRow({ alarm, onPatrolClick }: { alarm: AlarmEvent; onPatrolClick: () => void }) {
  const hasRoute = Number(alarm.point_count) > 0;
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className={`${CELL} whitespace-nowrap`}>{fmtDateTime(alarm.raised_at)}</td>
      <td className={CELL}><TypeBadge kind="alarm" onClick={hasRoute ? onPatrolClick : undefined} /></td>
      <td className={CELL}>{alarm.patrol_type ?? DASH}</td>
      <td className={`${CELL} whitespace-nowrap`}>{alarm.guard_name}</td>
      <td className={`${CELL} font-mono`}>{alarm.lat.toFixed(5)}, {alarm.lng.toFixed(5)}</td>
      <td className={`${CELL} whitespace-nowrap`}>{alarm.ack_at ? fmtDateTime(alarm.ack_at) : DASH}</td>
      {/* Both notes stack in the shared Details column rather than getting a
          column of their own, which would be blank for incidents and patrol
          reports. Labelled, because "what was seen" and "what was done" read
          as contradictory when run together unattributed. */}
      <td className={`${CELL} max-w-xs`}>
        {!alarm.notes && !alarm.ack_notes && DASH}
        {alarm.notes && (
          <div className="whitespace-pre-wrap">
            <span className="text-gray-400">Patroller: </span>{alarm.notes}
          </div>
        )}
        {alarm.ack_notes && (
          <div className="whitespace-pre-wrap text-gray-600">
            <span className="text-gray-400">Control room: </span>{alarm.ack_notes}
          </div>
        )}
      </td>
    </tr>
  );
}

/** Incident row: GPS populated, no Ack */
function IncidentRow({ incident, onPatrolClick }: { incident: IncidentEvent; onPatrolClick: () => void }) {
  const hasRoute = Number(incident.point_count) > 0;
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className={`${CELL} whitespace-nowrap`}>{fmtDateTime(incident.raised_at)}</td>
      <td className={CELL}><TypeBadge kind="incident" onClick={hasRoute ? onPatrolClick : undefined} /></td>
      <td className={CELL}>{incident.patrol_type ?? DASH}</td>
      <td className={`${CELL} whitespace-nowrap`}>{incident.guard_name}</td>
      <td className={`${CELL} font-mono`}>{incident.lat.toFixed(5)}, {incident.lng.toFixed(5)}</td>
      <td className={CELL}>{DASH}</td>
      <td className={`${CELL} max-w-xs`}>{incident.notes ?? DASH}</td>
    </tr>
  );
}

/** Patrol Report row: no GPS, no Ack */
function ReportRow({ patrol, onPatrolClick }: { patrol: PatrolSession; onPatrolClick: () => void }) {
  const hasRoute = Number(patrol.point_count) > 0;
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className={`${CELL} whitespace-nowrap`}>{fmtDateTime(patrol.ended_at ?? patrol.started_at)}</td>
      <td className={CELL}><TypeBadge kind="report" onClick={hasRoute ? onPatrolClick : undefined} /></td>
      <td className={CELL}>{patrol.patrol_type ?? DASH}</td>
      <td className={`${CELL} whitespace-nowrap`}>{patrol.guard_name}</td>
      <td className={CELL}>{DASH}</td>
      <td className={CELL}>{DASH}</td>
      <td className={`${CELL} max-w-xs`}>{patrol.incident_notes ?? DASH}</td>
    </tr>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function EventsPage({ isAdmin = false }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role()) ?? 'admin';

  const [guards, setGuards]           = useState<Guard[]>([]);
  const [guardFilter, setGuardFilter] = useState(searchParams.get('guard_id') ?? '');
  const [page, setPage]               = useState(1);
  const [patrols, setPatrols]         = useState<PatrolSession[]>([]);
  const [alarms, setAlarms]           = useState<AlarmEvent[]>([]);
  const [incidents, setIncidents]     = useState<IncidentEvent[]>([]);
  const [alarmTotal, setAlarmTotal]   = useState(0);
  const [incidentTotal, setIncidentTotal] = useState(0);
  const [reportTotal, setReportTotal] = useState(0);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    (isAdmin ? adminApi.listGuards() : portalApi.listGuards())
      .then(setGuards)
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    setLoading(true);
    const gId = guardFilter || undefined;
    Promise.all([
      patrolApi.list(page, gId),
      isAdmin ? adminApi.listAlarms(page, gId) : portalApi.listAlarms(page, gId),
      isAdmin ? adminApi.listIncidents(page, gId) : portalApi.listIncidents(page, gId),
    ])
      .then(([patrolResult, alarmResult, incidentResult]) => {
        setPatrols(patrolResult.data.filter((p) => p.outcome === 'incident'));
        setAlarms(alarmResult.data);
        setIncidents(incidentResult.data);
        setReportTotal(patrolResult.total);
        setAlarmTotal(alarmResult.total);
        setIncidentTotal(incidentResult.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, page, guardFilter]);

  const events = useMemo<EventItem[]>(() => {
    const items: EventItem[] = [
      ...alarms.map((a): AlarmItem => ({
        kind: 'alarm',
        ts: toMs(a.raised_at),
        patrolId: a.patrol_id,
        data: a,
      })),
      ...incidents.map((i): IncidentItem => ({
        kind: 'incident',
        ts: toMs(i.raised_at),
        patrolId: i.patrol_id,
        data: i,
      })),
      ...patrols.map((p): ReportItem => ({
        kind: 'report',
        ts: toMs(p.ended_at ?? p.started_at),
        patrolId: p.id,
        data: p,
      })),
    ];
    return items.sort((a, b) => b.ts - a.ts);
  }, [alarms, incidents, patrols]);

  const alarmCount    = alarms.length;
  const incidentCount = incidents.length;
  const reportCount   = patrols.length;

  // Total pages = max ceiling across the three sources
  const perPage = 50;
  const totalPages = Math.max(
    Math.ceil(alarmTotal / perPage),
    Math.ceil(incidentTotal / perPage),
    Math.ceil(reportTotal / perPage),
    1,
  );

  function handleGuardFilter(value: string) {
    setGuardFilter(value);
    setPage(1);
    setSearchParams(value ? { guard_id: value } : {});
  }

  function goToPatrol(patrolId: string) {
    navigate(`/${role}/patrols?replay=${patrolId}`);
  }

  return (
    <div className="px-6 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-gray-900">Events</h1>
        <p className="text-sm text-gray-500">
          {loading ? 'Loading…' : (
            <>
              {alarmCount} alarm{alarmCount !== 1 ? 's' : ''}
              {' · '}
              {incidentCount} incident{incidentCount !== 1 ? 's' : ''}
              {' · '}
              {reportCount} patrol report{reportCount !== 1 ? 's' : ''}
              {guardFilter ? ' · filtered' : ''}
            </>
          )}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={guardFilter}
          onChange={(e) => handleGuardFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        >
          <option value="">All patrollers</option>
          {guards.map((g) => (
            <option key={g.id} value={g.id}>
              {g.display_name}
            </option>
          ))}
        </select>
        {guardFilter && (
          <button
            onClick={() => handleGuardFilter('')}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Event feed */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading events…</div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center text-gray-400">No events recorded.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 font-semibold text-gray-600 w-36">Date</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 w-36">Event Type</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Patrol Type</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Patroller</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">GPS</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 w-36">Ack</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Details</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) =>
                  e.kind === 'alarm' ? (
                    <AlarmRow
                      key={`alarm-${e.data.id}`}
                      alarm={e.data}
                      onPatrolClick={() => goToPatrol(e.patrolId)}
                    />
                  ) : e.kind === 'incident' ? (
                    <IncidentRow
                      key={`incident-${e.data.id}`}
                      incident={e.data}
                      onPatrolClick={() => goToPatrol(e.patrolId)}
                    />
                  ) : (
                    <ReportRow
                      key={`report-${e.data.id}`}
                      patrol={e.data}
                      onPatrolClick={() => goToPatrol(e.patrolId)}
                    />
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
